import {useCallback, useEffect, useState} from 'react'
import {chatService} from '@/services/chatService'
import {onTripMessage} from '@/services/reverb'

/**
 * THE THREAD — history, live arrivals, and sending.
 *
 * ── ⚠️ IT IS NEVER FETCHED IN THE BACKGROUND ──────────────────────────────────────
 * Fetching marks the driver's messages read, with no way to opt out. So nothing here reads
 * the thread unless it is open and on screen: a background poll clears the unread badge for
 * someone who never looked, which is exactly the bug the driver portal had.
 *
 * While it IS open, re-reading is not a side effect — those messages are being read.
 *
 * ── ⚠️ A SOCKET EVENT IS A HINT, NEVER THE SOURCE OF TRUTH ────────────────────────
 * The payload is used where it parses, because inserting one message is far cheaper than
 * re-reading a thread that would mark things read again. Anything unparseable falls back
 * to nothing rather than to a refetch, and the visibility refetch catches what was missed.
 */
/** How often an open thread re-reads itself to bring read receipts up to date. Slow
    enough to be cheap, fast enough that a tick turning green feels like a response. */
const RECEIPT_REFRESH_MS = 15000

export function useTripChat(tripId, {enabled = true} = {}) {
    const [messages, setMessages] = useState([])
    const [pagination, setPagination] = useState(null)
    const [loading, setLoading] = useState(true)
    const [sending, setSending] = useState(false)
    const [error, setError] = useState('')

    /*
     * WHERE IN THE THREAD WE ARE, AND WHICH WAY "OLDER" LIES — see `getLatestConversation`.
     *
     * ⚠️ STATE, NOT REFS, BECAUSE `hasMore` IS DRAWN FROM IT. A ref read during render is
     * a value React has not been told changed: the "Load earlier messages" button would go
     * on saying whatever it said when something else last re-rendered the thread.
     */
    const [cursor, setCursor] = useState({page: 1, lastPage: 1, oldestFirst: false})

    const merge = useCallback((incoming) => {
        setMessages((prev) => {
            const byId = new Map(prev.map((m) => [m.id, m]))
            incoming.forEach((m) => byId.set(m.id, m))
            return [...byId.values()].sort((a, b) => b.id - a.id)
        })
    }, [])

    const load = useCallback(async (page = 1) => {
        const result = await chatService.getConversation(tripId, page)
        if (!result.success) return result

        setPagination(result.pagination)
        setMessages((prev) => {
            /* ⚠️ MERGED BY ID, NOT REPLACED. A refetch overlaps whatever the socket has
               already inserted, and the same message arriving twice must not appear twice.
               Page 1 also has to keep the older pages already scrolled into view. */
            const byId = new Map(prev.map((m) => [m.id, m]))
            result.messages.forEach((m) => byId.set(m.id, m))
            return [...byId.values()].sort((a, b) => b.id - a.id)
        })
        return result
    }, [tripId])

    /*
     * THE NEWEST PAGE ON OPEN. Not on an interval — see the note above.
     *
     * ⚠️ "THE NEWEST PAGE" IS NOT NECESSARILY PAGE 1, and assuming it was is a bug that
     * hides itself: the thread opens on a full page of genuine messages from last week and
     * the newest one is simply not in it. The two sides' docs disagree about the order, so
     * `getLatestConversation` determines it instead of trusting either — one extra request
     * at most, and only when the answer is not already obvious.
     */
    useEffect(() => {
        if (!enabled || !tripId) return undefined
        let cancelled = false
        setLoading(true)
        setMessages([])

        chatService.getLatestConversation(tripId)
            .then((result) => {
                if (cancelled || !result.success) return
                setCursor({page: result.page, lastPage: result.lastPage, oldestFirst: result.oldestFirst})
                setPagination(result.pagination)
                merge(result.messages)
            })
            .finally(() => { if (!cancelled) setLoading(false) })
        return () => { cancelled = true }
    }, [enabled, tripId, merge])

    /* Live arrivals. The service binds both spellings of the event name, because
       `listen()` matches exactly and binding one form only is a silent failure. */
    useEffect(() => {
        if (!enabled || !tripId) return undefined
        return onTripMessage(tripId, (message) => {
            if (!message) return
            setMessages((prev) => (prev.some((m) => m.id === message.id)
                ? prev
                : [message, ...prev]))

            /* ⚠️ AND RE-READ, FOR THE RECEIPTS. A message arriving is proof the other
               party has the thread open, which means they have just read everything the
               reader sent — but `is_read` lives on the MESSAGE, and nothing broadcasts a
               change to it. Only a fetch can bring the ticks up to date. */
            load(1)
        })
    }, [enabled, tripId, load])

    /*
     * ── RECEIPTS, WHILE THE THREAD IS OPEN ────────────────────────────────
     *
     * ⚠️ THIS IS NOT THE POLL THE GUIDE FORBIDS, and the difference is the whole
     * justification. What must never happen is fetching the conversation in the BACKGROUND,
     * because the fetch marks the other party's messages read and the unread badge then
     * drops to zero for someone who never looked. Here the thread is open and on screen:
     * those messages ARE being read, so marking them read is not a side effect, it is the
     * truth.
     *
     * ⚠️ AND IT IS THE ONLY WAY A TICK EVER TURNS GREEN. `is_read` changes server-side
     * when the other party opens the thread, and nothing broadcasts that change. Without a
     * re-read, a message sent and read stays on one tick until the page is reloaded.
     *
     * ⚠️ STOPS WITH THE TAB. A hidden tab is not being read by anyone.
     */
    useEffect(() => {
        if (!enabled || !tripId) return undefined
        const timer = setInterval(() => {
            if (document.visibilityState === 'visible') load(1)
        }, RECEIPT_REFRESH_MS)
        return () => clearInterval(timer)
    }, [enabled, tripId, load])

    /* ⚠️ AND ON TAB-VISIBLE. The socket drops while backgrounded and nothing queues what
       was sent in that window; without this the thread silently misses messages. */
    useEffect(() => {
        if (!enabled || !tripId) return undefined
        const onVisible = () => { if (document.visibilityState === 'visible') load(1) }
        document.addEventListener('visibilitychange', onVisible)
        return () => document.removeEventListener('visibilitychange', onVisible)
    }, [enabled, tripId, load])

    /**
     * Older messages, for a thread scrolled back to its top.
     *
     * ⚠️ WHICH WAY "OLDER" LIES DEPENDS ON THE ORDER. Oldest-first, older is the
     * PREVIOUS page; newest-first it is the next one. Hard-coding `page + 1` walked forward
     * into newer messages on a server paginating the other way — and since those pages are
     * merged by id, the result was a thread that appeared to load nothing at all.
     *
     * ⚠️ AND `has_more_pages` IS NOT TRUSTED ALONE. It is absent from some responses
     * entirely; the page bounds are the reliable test and are checked either way.
     */
    const loadMore = useCallback(async () => {
        const next = cursor.oldestFirst ? cursor.page - 1 : cursor.page + 1
        if (next < 1 || next > cursor.lastPage) return

        setCursor((c) => ({...c, page: next}))
        await load(next)
    }, [load, cursor])

    const send = useCallback(async (text) => {
        setSending(true)
        setError('')
        const result = await chatService.send(tripId, text)
        setSending(false)

        if (!result.success) {
            /* ⚠️ THE FAILURE IS RETURNED, NOT SWALLOWED, so the composer can keep the text.
               Losing a typed message to a failed request is worse than the failure. */
            setError(result.error || 'That message could not be sent.')
            return false
        }

        /* Optimistic in the sense that matters: the sender sees it immediately rather than
           waiting for the socket to echo it back. De-duped by id when it does. */
        if (result.data) {
            setMessages((prev) => (prev.some((m) => m.id === result.data.id) ? prev : [result.data, ...prev]))
        }
        return true
    }, [tripId])

    return {
        messages,
        loading,
        sending,
        error,
        hasMore: cursor.oldestFirst
            ? cursor.page > 1
            : (cursor.page < cursor.lastPage || !!pagination?.has_more_pages),
        loadMore,
        send,
        clearError: () => setError(''),
    }
}

export default useTripChat
